import { Component, inject, Input, LOCALE_ID, OnDestroy, OnInit, signal } from "@angular/core";
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from "@angular/forms";
import { ConfirmationService, MenuItem, PrimeIcons } from "primeng/api";
import { DialogService } from "primeng/dynamicdialog";
import { forkJoin, lastValueFrom, Observable, of, Subscription } from "rxjs";
import { catchError, debounceTime, map } from "rxjs/operators";
import { Fluid, FluidType } from "src/app/perical/models/fluid.model";
import { CustomConnectionModel } from "src/app/shared/models/custom-connection.model";
import { ChangeSelectedTubular, DataSavedMessage } from "src/app/shared/models/mediator-messages.model";
import { MediatorService } from "src/app/shared/services/mediator.service";
import { PeriforOnChangeMessages, SignalRService } from "src/app/shared/services/signal-r.service";
import { TrajectoryService } from "src/app/shared/services/trajectory.service";
import { getControlErrors, isControlInvalid } from "src/app/shared/services/validation-helpers";
import { WellConfigService } from "src/app/shared/services/well-config.service";
import { WellTypeService } from "../../services/well-type-datums.service";
import { GradeReference, PipeReference, Tubular, WellComponent } from "./models";
import { greaterThanZero } from "./validation/greaterThanZero";
import { hangerAbovePreviousShoe } from "./validation/hangerAbovePreviousShoe";
import { holeSizeSmallerThanPrevsId } from "./validation/holeSizeSmallerThanPrevsId";
import { measuredDepthMustBeGreaterThanPrevious } from "./validation/measureDepthMustBeGreaterThanPrevious";
import { revalidateTubulars } from "./validation/revalidateTubulars";
import { shoeValidator } from "./validation/shoeValidator";
import { topOfCementMdRequired } from "./validation/topOfCementRequired";
import { getMinimumDiameterFittingWellbore, getWellboreOutsideString, stringSectionsMustFitInWellbore } from "./validation/tubularFitsInWellConfiguration";
import { TotalDepthResult } from "src/app/shared/models/trajectory.model";
import { UserUnitsModel } from "src/app/core/components/user-units/user-units.model";
import { UserRoles } from "src/app/core/components/user-admin-page/user-model";
import { Utilities } from "src/app/core/services/utilities";
import { StorageKeys, StoreService } from "src/app/core/services/store.service";
import { GetShortLengthValueFromInches, GetValueFromPpg, GetValueFromPsi } from "src/app/perivis/shared/helpers/units.helper";
import { CementingLandingService } from "src/app/shared/services/cementing-landing.service";
import { FluidsService } from "src/app/perical/services/fluids.service";
import { unitsLib } from "src/app/core/services/unit-library";
import { LoadCaseService } from "src/app/perivis/services/load-case.service";
import { isEqual } from 'lodash-es';
import { formatUnitNumber } from "src/app/shared/helpers/unit-number-format.helper";
import { UnitPrecisionOverrides } from "src/app/shared/helpers/unit-precision.helper";

@Component({
  selector: "app-well-configuration",
  templateUrl: "./well-configuration.component.html",
  providers: [DialogService, ConfirmationService],
  standalone: false
})
export class WellConfigurationComponent implements OnInit, OnDestroy {

  private _wellComponentsData: WellComponent[];
  private _subscriptions: Subscription = new Subscription();
  private _pipeCatalog: PipeReference[];
  private tubularStrings;
  private selectedRowIdx: number;
  private wellCmpFg: any;
  private tubularsBackup: WellComponent[];
  private _nonApiPipeExpandedDict: { tubularIndex: number, stringSections: { stringSectionIndex: number, isExpanded: boolean }[] }[] = [];
  private _forceUnit: string;
  private _densityUnit: string;
  private _shortLengthUnit: string;
  private _pressureUnit: string;
  private _allowSave = true;
  private readonly _expandedRowsStoragePrefix = "well-config-expanded-rows";
  private readonly _locale = inject(LOCALE_ID);

  public totalDepth: TotalDepthResult;
  public grades: { label: string, value: GradeReference }[] = [];
  public connections: { label: string, value: CustomConnectionModel }[] = [];
  public pipeOds: { label: string, value: number }[] = [];
  public wellConfigForm: UntypedFormGroup;
  public isLoading = signal<boolean>(false);
  public readonly pipeDimensionPrecisionOverrides = signal<UnitPrecisionOverrides>({});
  public readonly wpfPrecisionOverrides = signal<UnitPrecisionOverrides>({});
  public wellheadDepth: number;
  public mudlineDepth: number;
  public wellType: string;
  public drillFloorElevation: number;
  public selectedStringIndex: number;
  public fluids: { label: string, value: string }[];
  public contextMenuItems: MenuItem[];
  public tubularCols: { field: string, header: string }[];
  public cols: { field: string, header: string }[];
  public nonApiPipeInputs: { field: string, header: string }[];
  public highCollapsePipeLabel: string;
  public userRoles: UserRoles;
  public longLengthUnit: string;
  public units: UserUnitsModel;
  public tableHeight: string = '600px';
  public displayAnnContentsInfo: boolean;
  public expandedRows: Record<string, boolean> = {};

  get wellComponents(): UntypedFormArray {
    return this.wellConfigForm.get("wellComponents") as UntypedFormArray;
  }

  // Validation delegates
  public isControlInvalid: Function = isControlInvalid;
  public getControlErrors: Function = getControlErrors;

  public stringTypes = [
    { label: "Casing", value: "Casing" },
    { label: "Tieback", value: "Tieback" },
    { label: "Liner", value: "Liner" },
    { label: "Tubing", value: "Tubing" }
  ];

  // Used for component state
  @Input()
  public componentId: string;

  constructor(
    private _trajectoryService: TrajectoryService,
    private _wellConfigService: WellConfigService,
    private _formBuilder: UntypedFormBuilder,
    private _confirmationService: ConfirmationService,
    private _wellTypeService: WellTypeService,
    private _signalRService: SignalRService,
    private _store: StoreService,
    private _messenger: MediatorService,
    private _cementingLandingService: CementingLandingService,
    private _fluidsService: FluidsService,
    private _loadCaseService: LoadCaseService,
  ) { }

  async ngOnInit(): Promise<void> {
    this.isLoading.set(true);

    this.wellConfigForm = this._formBuilder.group({
      wellComponents: this._formBuilder.array([]),
    });

    this.units = await this._store.get<UserUnitsModel>(StorageKeys.UNITS);
    this.pipeDimensionPrecisionOverrides.set({ [this.units.shortLengths]: 3 });
    this.wpfPrecisionOverrides.set({ [this.units.linearDensity]: 3 });
    this._forceUnit = this.units.force;
    this._densityUnit = unitsLib[this.units.linearDensity].symbol;
    this._shortLengthUnit = this.units.shortLengths;
    this.longLengthUnit = this.units.longLengths;
    this._pressureUnit = this.units.pressure;
    this.userRoles = await this._store.get<UserRoles>(StorageKeys.ROLES);

    this.contextMenuItems = [
      { label: 'Add String', icon: PrimeIcons.PLUS, command: () => { this.onAddWellComponent() } },
      { label: 'Insert string above', icon: PrimeIcons.ARROW_UP, command: () => { this.onAddWellComponent(this.selectedRowIdx) } },
      { label: 'Delete String', icon: PrimeIcons.TRASH, command: () => { this.onDeleteWellComponent(this.selectedRowIdx, this.wellCmpFg) } },
    ];

    this.tubularCols = [
      { field: 'bottomMeasuredDepth', header: `Bottom (${this.longLengthUnit} MD)` },
      { field: 'outsideDiameter', header: `OD (${this._shortLengthUnit})` },
      { field: 'weightPerFoot', header: `WPF (${this._densityUnit})` },
      { field: 'insideDiameter', header: `ID (${this._shortLengthUnit})` },
      { field: 'wallThickness', header: `Wall (${this._shortLengthUnit})` },
      { field: 'driftDiameter', header: `Drift (${this._shortLengthUnit})` },
      { field: 'grade', header: 'Grade' },
      { field: 'wallPerformance', header: 'Ratings' },
      { field: 'connection', header: 'Grade / Connection' }
    ];

    this.cols = [
      { field: 'type', header: 'Type' },
      { field: 'hangerMd', header: `Hanger (${this.longLengthUnit} MD)` },
      { field: 'shoeMd', header: `Shoe (${this.longLengthUnit} MD)` },
      { field: 'holeSize', header: `Hole Size (${this._shortLengthUnit})` },
      { field: 'topOfCementMd', header: `TOC (${this.longLengthUnit} MD)` },
      { field: 'annularFluid', header: 'Fluid' }
    ];

    this.nonApiPipeInputs = [
      { field: 'tension', header: `Tension (${this._forceUnit})` },
      { field: 'compression', header: `Compression (${this._forceUnit})` },
      { field: 'burst', header: `Burst (${this._pressureUnit})` },
      { field: 'collapse', header: `Collapse (${this._pressureUnit})` },
      { field: 'wallToleranceHoop', header: 'Wall % (Triax-Hoop)' },
      { field: 'wallToleranceTriaxialAxial', header: 'Wall % (Triax-Axial)' },
      { field: 'wallToleranceAxial', header: 'Wall % (Axial)' },
      { field: 'wallToleranceBurst', header: 'Wall % (Burst)' },
      { field: 'wallToleranceCollapse', header: 'Wall % (Collapse)' }
    ];

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));

    this.getData(false);

    this.onWellConfigFormChanges();

    addEventListener("keydown", (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
      }
    });
  }

  async signalRfunc(data: any): Promise<any> {
    if (data.action == PeriforOnChangeMessages.REFRESH_FLUIDS) {
      await lastValueFrom(this._fluidsService.getFluids()).then((fluids) => {
        this.fluids = fluids
          .filter(f => f.state.type == FluidType.BRINE || f.state.type == FluidType.STANDARDMUD || f.state.type == FluidType.ADVANCEDMUD)
          .sort((a, b) => a.state.name.localeCompare(b.state.name))
          .map(f => ({ label: f.state.name, value: f.id }));
      });
    }

    if (data.action == PeriforOnChangeMessages.REFRESH_WELL_CONFIG ||
      data.action == PeriforOnChangeMessages.REFRESH_TRAJECTORY) {
      this.getData(true);
    }

    if (data.action == PeriforOnChangeMessages.REFRESH_PIPES_CATALOG) {
      const pipes = await lastValueFrom(this._wellConfigService.getPipeCatalog());
      this.processPipeData(pipes);
      this.wellComponents.controls.forEach((wellCmpFg: UntypedFormGroup) => { // Update all the pipe lookups
        this.setPipeSelectOptions(wellCmpFg);
      });
    }

    if (data.action == PeriforOnChangeMessages.REFRESH_GRADES_CATALOG || data.action == PeriforOnChangeMessages.REFRESH_CONNECTIONS_CATALOG) {
      this.getData(false);
    }

    if (data.action == PeriforOnChangeMessages.REFRESH_STRING_INPUTS) {
      this._wellConfigService.getTubular().subscribe(tubular => {
        this.selectedStringIndex = tubular.sequenceNumber;
      });
    }
  }

  getRowIndex(row: any, wellCmpFg: any) {
    this.selectedRowIdx = row;
    this.wellCmpFg = wellCmpFg;
  }

  getData(calledFromSignalR: boolean) {
    this.isLoading.set(true);

    const expandedRowsBeforeReload = this.getExpandedRowsFromCurrentForm();
    const persistedExpandedRows = this.readExpandedRowsFromStorage();
    this._nonApiPipeExpandedDict = [];

    const sources: Observable<any>[] = [
      this._wellConfigService.getConnectionsList() as Observable<any>,
      this._wellConfigService.getTubulars() as Observable<any>,
      this._wellConfigService.getPipeCatalog() as Observable<any>,
      this._wellConfigService.getGradeCatalog() as Observable<any>,
      this._wellConfigService.getFluidsList() as Observable<any>,
      this._trajectoryService.getTotalDepth() as Observable<any>,
      this._wellTypeService.getWellType() as Observable<any>
    ];

    forkJoin(sources).pipe(
      map(([connections, tubulars, pipes, grades, fluids, totalDepth, wellType]) => {
        this.tubularStrings = tubulars;
        this.wellheadDepth = wellType.wellheadDepthFromDrillFloor;
        this.drillFloorElevation = wellType.drillFloorElevation;
        this.mudlineDepth = wellType.drillFloorElevation + wellType.waterDepth;
        this.wellType = wellType.type;
        this.totalDepth = totalDepth;
        this.fluids = fluids
          .filter(f => f.state.type == FluidType.BRINE || f.state.type == FluidType.STANDARDMUD || f.state.type == FluidType.ADVANCEDMUD)
          .sort((a, b) => a.state.name.localeCompare(b.state.name))
          .map(f => ({ label: f.state.name, value: f.id }));

        this.processGradeData(grades);
        this.processPipeData(pipes);
        this.processConnectionData(connections);

        this.tubularsBackup = [...tubulars];

        if (this.wellComponents.value.length > 0) {
          let tubularIndex = 0;
          this.wellComponents.value.forEach(tubular => {
            let ssIndex = 0;
            if (tubular.stringSections.find(x => x.isNonApiPipeExpanded)) {
              tubular.stringSections.forEach(stringSection => {
                this._nonApiPipeExpandedDict.push({ tubularIndex: tubularIndex, stringSections: [{ stringSectionIndex: ssIndex, isExpanded: stringSection.isNonApiPipeExpanded }] });
                ssIndex++;
              });
            }
            tubularIndex++;
          });
        }

        // if (!calledFromSignalR) {
        this.wellComponents.clear();
        this.processTubularData(tubulars);
        this.applyExpandedRows({ ...persistedExpandedRows, ...expandedRowsBeforeReload });

        this._nonApiPipeExpandedDict.forEach(x => {
          this.wellComponents.controls[x.tubularIndex]['controls'].stringSections.controls[x.stringSections[0].stringSectionIndex]['controls']['isNonApiPipeExpanded'].setValue(x.stringSections[0].isExpanded, { emitEvent: false });
        });
        // }

        if (tubulars.length > 0) {
          this._wellConfigService.getTubular().subscribe(tubular => {
            this.selectedStringIndex = tubular.sequenceNumber;
          });
        } else {
          this.selectedStringIndex = null;
        }

        if (calledFromSignalR) {
          this.wellConfigForm.controls.wellComponents['controls'].forEach(element => {
            element.controls.shoeMd.setValidators(Validators.max(this.totalDepth.measuredDepth));
          });
        }
        this.isLoading.set(false);
      }),
      catchError(err => {
        this.isLoading.set(false);
        return of(null);
      })).subscribe();
  }

  public stringSectionsExpand(expanded: boolean, index: number): void {
      const wellCmpFg = this.wellComponents.controls[index] as UntypedFormGroup;
      const type = wellCmpFg.get("type").value;
      const holeSize = wellCmpFg.get("holeSize").value;
      if ((type == "Casing" || type == "Liner") && (!holeSize || holeSize <= 0)) {
        return;
      }
    if (!expanded) {
      this._messenger.publish(new ChangeSelectedTubular(this.tubularStrings[index]));

      this.wellCmpFg.controls.stringSections.controls.forEach(element => {
        element.get("isNonApiPipeExpanded").setValue(false, { emitEvent: false });
      });
    }

    this.wellComponents.controls.forEach((element, i) => {
      if (i !== index) {
        element.get("stringSectionExpanded").setValue(false, { emitEvent: false });
      } else {
        element.get("stringSectionExpanded").setValue(!expanded, { emitEvent: false });
      }
    });
    this.getExpandedRowKeys();
    this.saveExpandedRowsToStorage();
  }

  public getExpandedRowKeys(): void {
    const expandedRowKeys: Record<string, boolean> = {};

    this.wellComponents?.value?.forEach(c => {
      expandedRowKeys[c.id] = c.stringSectionExpanded ?? false;
    });
    this.expandedRows = expandedRowKeys;
  }

  private getExpandedRowsStorageKey(): string {
    return `${this._expandedRowsStoragePrefix}:${this.componentId ?? "default"}`;
  }

  private saveExpandedRowsToStorage(): void {
    try {
      localStorage.setItem(this.getExpandedRowsStorageKey(), JSON.stringify(this.expandedRows));
    } catch {
      // No-op: localStorage may be unavailable.
    }
  }

  private readExpandedRowsFromStorage(): Record<string, boolean> {
    try {
      const persisted = localStorage.getItem(this.getExpandedRowsStorageKey());
      if (!persisted) {
        return {};
      }

      const parsed = JSON.parse(persisted) as Record<string, boolean>;
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }

  private getExpandedRowsFromCurrentForm(): Record<string, boolean> {
    const expandedRows: Record<string, boolean> = {};

    this.wellComponents?.controls?.forEach((wellCmpFg: UntypedFormGroup) => {
      const id = wellCmpFg.get("id")?.value;
      if (!id) {
        return;
      }

      expandedRows[id] = !!wellCmpFg.get("stringSectionExpanded")?.value;
    });

    return expandedRows;
  }

  private applyExpandedRows(expandedRows: Record<string, boolean>): void {
    const normalizedExpandedRows: Record<string, boolean> = {};

    this.wellComponents?.controls?.forEach((wellCmpFg: UntypedFormGroup) => {
      const id = wellCmpFg.get("id")?.value;
      if (!id) {
        return;
      }

      const isExpanded = !!expandedRows[id];
      wellCmpFg.get("stringSectionExpanded")?.setValue(isExpanded, { emitEvent: false });
      normalizedExpandedRows[id] = isExpanded;
    });

    this.expandedRows = normalizedExpandedRows;
    this.saveExpandedRowsToStorage();
  }

  public onAddTubular(wellCmp: UntypedFormGroup, tIdx: number, isBelow?: boolean): void {
    const lowerIndex = isBelow ? 1 : 0;

    const newTubular = this.newTubular(wellCmp.get("hangerMd").value);

    //Set default values for pipe on the tubular added based on the current pipe.
    const stringSection = wellCmp.get('stringSections')?.value[tIdx] as Tubular;

    this.onPipeIdChange(stringSection.pipe.id, newTubular);
    newTubular.get("grade").patchValue(stringSection.grade);
    newTubular.get("connection").patchValue(stringSection.connection);

    if (tIdx == 0) {
      const defaultDepth = wellCmp.get("hangerMd").value;
      newTubular.get("bottomMeasuredDepth").patchValue(defaultDepth + 1);
    }

    const index = tIdx + lowerIndex;
    newTubular.patchValue({ sequenceNumber: index });

    this.getWellComponentTubulars(wellCmp).insert(index || 0, newTubular);
    this.onTubularInteractions(wellCmp, true, index, isBelow);
  }

  public onDeleteTubular(wellCmp: UntypedFormGroup, tubularIdx: number): void {
    this.getWellComponentTubulars(wellCmp).removeAt(tubularIdx);
  }

  public onAddWellComponent(idx?: number): void {
    if (this.wellComponents.invalid) {
      return;
    }
    this.wellComponents.markAllAsTouched();
    const index = idx >= 0 ? idx : this.wellComponents.length;
    const fg = this.newWellComponent([this.newTubular(this.wellComponents[index - 1]?.controls.hangerMd.value ?? this.wellheadDepth)]);
    fg.get("sequenceNumber")?.setValue(index, { emitEvent: false })
    this.wellComponents.insert(index, fg);

    this.wellComponents.controls.forEach(element => {
      element.get("stringSectionExpanded")?.setValue(false, { emitEvent: false });
    });

    this.getExpandedRowKeys();
    this.saveExpandedRowsToStorage();
  }

  public onDeleteWellComponent(wellCmpIdx: number, wellCmpFg: UntypedFormGroup): void {
    const tubularId = wellCmpFg.get("id").value;
    if (tubularId.length < 36) {
      this.wellComponents.removeAt(wellCmpIdx);
      this.getExpandedRowKeys();
      this.saveExpandedRowsToStorage();
    } else {
      this._confirmationService.confirm({
        message: 'Are you sure that you want to delete the selected string?',
        accept: async () => {
          await lastValueFrom(this._wellConfigService.deleteTubular(tubularId));
          this._messenger.publish(new DataSavedMessage('Well Configuration'));
          this.wellComponents.removeAt(wellCmpIdx);
          this.getExpandedRowKeys();
          this.saveExpandedRowsToStorage();
          const outermostStringId = this.wellComponents.controls.length > 0 ? this.wellComponents.controls[0].get("id").value : null;
          this._messenger.publish(new ChangeSelectedTubular(new Tubular({ id: outermostStringId })));
        }
      });
    }
  }

  public onHasCementChange(hasCement: boolean, wellCmpFg: UntypedFormGroup): void {
    const topCementCtrl = wellCmpFg.get("topOfCementMd");
    if (hasCement) {
      const toc = topCementCtrl?.value ?? -1 >= 0 ? topCementCtrl.value : this.wellheadDepth;
      topCementCtrl.patchValue(toc, { emitEvent: false }); //Handles reloading right value
      topCementCtrl.enable({ emitEvent: false });
    } else {
      topCementCtrl.disable({ emitEvent: false });
      topCementCtrl.patchValue(null, { emitEvent: false });
    }
  }

  public onPipeIdChange(pipeId: string, tubularFg: UntypedFormGroup): void {
    const pipeCtrl = tubularFg.get('pipe') as UntypedFormControl;
    pipeCtrl.get("id").patchValue(pipeId, { emitEvent: false });

    const pipe = this._pipeCatalog ? this._pipeCatalog.find(p => p.id == pipeId) : [];

    if (pipe) {
      pipeCtrl.patchValue(pipe, { emitEvent: false });
      pipeCtrl.updateValueAndValidity({ emitEvent: false });
    }
  }

  public async onPipeOdChange(pipeOd: number, tubularFg: UntypedFormGroup): Promise<void> {
    if (pipeOd) {
      const pipeCtrl = tubularFg.get('pipe') as UntypedFormControl;
      const pipe = this._pipeCatalog.find(p => p.outsideDiameter == pipeOd);
      pipeCtrl.patchValue(pipe, { emitEvent: false });
    }
  }

  public onTubularInteractions(wellCmpFg: UntypedFormGroup, isAdd: boolean, index?: number, isBelow?: boolean): void {
    const tubulars = this.getWellComponentTubulars(wellCmpFg);
    const lastTubular = tubulars.controls[tubulars.length - 1] as UntypedFormGroup;
    const depth = wellCmpFg.controls['shoeMd'].value;
    const btmDepthCtrl = lastTubular.get('bottomMeasuredDepth');
    btmDepthCtrl.setValue(depth, { emitEvent: false });

    const hangerMd = wellCmpFg.get('hangerMd').value;
    const topOfCementCtrl = wellCmpFg.controls['topOfCementMd'];

    if (hangerMd > topOfCementCtrl.value) {
      topOfCementCtrl.setValue(hangerMd, { emitEvent: false });
    }

    if (isAdd) {
      if (index == 0 && !isBelow) {
        const newDepth = (hangerMd + tubulars.controls[1].get('bottomMeasuredDepth').value) / 2;
        const currentTubular = tubulars.controls[0] as UntypedFormGroup;
        const btmDepthCtrl = currentTubular.get('bottomMeasuredDepth');
        btmDepthCtrl.setValue(newDepth, { emitEvent: false });

        if (btmDepthCtrl) {
          btmDepthCtrl.setValue(newDepth, { emitEvent: false });
        }
        return;
      }

      if (index === tubulars.length - 1) {
        let depths = wellCmpFg.value.stringSections.map(s => s.bottomMeasuredDepth);
        depths = Array.from(new Set([hangerMd, ...depths]));
        const newDepth = (depths[depths.length - 2] + depths[depths.length - 1]) / 2 ;
        const prevBtmDepthCtrl = tubulars.controls[index - 1].get('bottomMeasuredDepth');
        prevBtmDepthCtrl.setValue(newDepth, { emitEvent: false });
        return;
      }

      let depths = wellCmpFg.value.stringSections.map(s => s.bottomMeasuredDepth);
      depths = [hangerMd, ...depths];
      const missingPointIndex = depths.findIndex(d => d === '' || d == null);
      const newDepth = index == 1 && isBelow ? (depths[1] + depths[3]) / 2 : (depths[missingPointIndex - 1] + depths[missingPointIndex + 1]) / 2;
      const currentTubular = tubulars.controls[index ?? 0] as UntypedFormGroup;
      const btmDepthCtrl = currentTubular.get('bottomMeasuredDepth');
      btmDepthCtrl.setValue(newDepth, { emitEvent: false });

      if (btmDepthCtrl) {
        btmDepthCtrl.setValue(newDepth, { emitEvent: false });
      }
    }

    // This should not need to happen, but UI isn't updating validation without this
    topOfCementCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  public onWellConfigFormChanges(): void {
    this._subscriptions.add(this.wellComponents.valueChanges.subscribe(() => {
      this.wellConfigForm.markAllAsTouched();
      this.wellConfigForm.updateValueAndValidity({ emitEvent: false });
    }));
  }

  public onWellComponentTypeChange(wellCmpFg: UntypedFormGroup): void {
    // If Well Component is Casing, it should always have a disabled HangerMd with a starting value at the wellhead
    if (this.wellComponents && this.wellComponents.length > 0) {
      // Casing
      const isCasing = wellCmpFg.get('type').value == "Casing";
      const hangerCtrl = wellCmpFg.get("hangerMd") as UntypedFormGroup;
      if (isCasing) {
        hangerCtrl.disable({ emitEvent: false })
      } else {
        hangerCtrl.enable({ emitEvent: false });
      }
      // Tubing - handle current well tubular
      const isTubing = wellCmpFg.get("type").value == "Tubing";
      const isTieback = wellCmpFg.get("type").value == "Tieback";
      const holeSizeCtrl = wellCmpFg.get("holeSize") as UntypedFormGroup;
      const hasCementCtrl = wellCmpFg.get("hasCement") as UntypedFormGroup;

      if (isTubing || isTieback) {
        holeSizeCtrl.disable({ emitEvent: false });
        holeSizeCtrl.patchValue(null, { emitEvent: false });
      } else {
        holeSizeCtrl.enable({ emitEvent: false });
        if (!holeSizeCtrl.value) {
          const x: any = 0;
          holeSizeCtrl.patchValue(x, { emitEvent: false });
        }
      }

      if (isTieback) {
        const linerHangers = this.wellComponents.value.filter(wc => wc.type == "Liner" || wc.type == "Casing").map(wc => wc.hangerMd);
        const shoeCtrl = wellCmpFg.get("shoeMd") as UntypedFormGroup;
        if (!linerHangers.some(x => Math.abs(x - shoeCtrl.value) < 0.0001)) {
          shoeCtrl.setValue(linerHangers[linerHangers.length - 1]);
        }
      }

      if (isTubing || isCasing || isTieback) {
        hangerCtrl.disable({ emitEvent: false })
      } else {
        hangerCtrl.enable({ emitEvent: false });
      }

      // hangerCtrl.setValue(isCasing || isTieback ? this.wellheadDepth : hangerCtrl.value, { emitEvent: false });
      hasCementCtrl.setValue((isTubing || isTieback) && (!wellCmpFg.controls.hasCement.value || wellCmpFg.status === "INVALID") ? false : hasCementCtrl.value, { emitEvent: false });
      this.onHasCementChange(hasCementCtrl.value, wellCmpFg);
    }
  }

  public enableHighCollapsePipeRatings(rowData: UntypedFormGroup, type: string, grade: UntypedFormControl): boolean {
    return rowData.value.nonApiPipe[type] == false || grade.value.strengthCalculationOption != "NonApi";
  }

  public tubingHangerChange(wellCmpFg: UntypedFormGroup): void {
    const hangerCtrl = wellCmpFg.get("hangerMd");
    hangerCtrl.setValue((hangerCtrl.value == 0 ? this.wellheadDepth : 0));

    this._confirmationService.confirm({
      header: 'Hanger Depth Change',
      icon: PrimeIcons.INFO_CIRCLE,
      message: 'Changing the hanger depth requires the following actions:<br><br>' +
        '• Re-specify the hanger pressures for all thermal operations and post-install load cases.<br>' +
        '• Re-check all the pump/choke pressure inputs.<br>' +
        '• Perform general QA/QC.',
      acceptLabel: 'OK',
      rejectVisible: false
    });
  }

  public onWellComponentChange(wellCmpFg: UntypedFormGroup, dataChanged?: boolean): void {
    if (this._allowSave == false) {
      return;
    }
    wellCmpFg.markAllAsTouched({ emitEvent: false });
    wellCmpFg.updateValueAndValidity({ emitEvent: false });

    this.onTubularInteractions(wellCmpFg, false);

    this.setPipeSelectOptions(wellCmpFg);

    if (wellCmpFg.valid && !this.isLoading() && dataChanged) {
      this.saveUpdateWellComponent(wellCmpFg);
    }
    // this.getExpandedRowKeys();
  }

  public getWellComponentTubulars(wellCmp: UntypedFormGroup): UntypedFormArray {
    return wellCmp.get("stringSections") as UntypedFormArray;
  }

  public getNonApiPipeData(wellCmp: UntypedFormGroup, tIdx: number): UntypedFormArray {
    const stringSections = wellCmp.get("stringSections") as UntypedFormArray;
    return stringSections.controls[tIdx].get("nonApiPipe") as UntypedFormArray;
  }

  public expandNonApiPipeProperties(wellCmpFg: UntypedFormGroup, tIdx: number): void {
    const isNonApiPipeExpanded = ((this.wellComponents.controls[this.wellComponents.controls.indexOf(wellCmpFg)] as UntypedFormGroup).get("stringSections") as UntypedFormArray)
      .controls[tIdx].get("isNonApiPipeExpanded").value;

    ((this.wellComponents.controls[this.wellComponents.controls.indexOf(wellCmpFg)] as UntypedFormGroup).get("stringSections") as UntypedFormArray)
      .controls[tIdx].get("isNonApiPipeExpanded").setValue(!isNonApiPipeExpanded, { emitEvent: false });
  }

  public checkIfNonApiPipeDataExpanded(wellCmp: UntypedFormGroup, tIdx: number): boolean {
    const stringSections = wellCmp.get("stringSections") as UntypedFormArray;
    return stringSections.controls[tIdx].get("isNonApiPipeExpanded").value;
  }

  public saveUpdateWellComponent(wellCmpFg: UntypedFormGroup): void {
    const wellCmpId = wellCmpFg.value.id;
    const wellCmpToSave = new WellComponent(wellCmpFg.getRawValue());
    wellCmpToSave.stringSections.forEach((tubular: Tubular) => {
      if (tubular.grade.strengthCalculationOption == "Api") {
        tubular.nonApiPipe.tensionEnabled = false;
        tubular.nonApiPipe.compressionEnabled = false;
        tubular.nonApiPipe.burstEnabled = false;
        tubular.nonApiPipe.collapseEnabled = false;
      }
    });
    if (wellCmpId.length == 24) { // Item created on the client, API
      this._wellConfigService.createNewTubular(wellCmpToSave).subscribe(res => {
        wellCmpFg.controls["id"].patchValue(res, { emitEvent: false });
        wellCmpFg.controls["stringSectionExpanded"].setValue(true, { emitEvent: false });
        this.getExpandedRowKeys();
        this._messenger.publish(new ChangeSelectedTubular(new Tubular({ id: res })));
        const updatedExpandedRows = {};
        Object.keys(this.expandedRows).forEach(key => {
          if (key.length == 24 && key == wellCmpId) {
            updatedExpandedRows[res] = this.expandedRows[key];
          } else {
            updatedExpandedRows[key] = this.expandedRows[key];
          }
        });
        this.expandedRows = updatedExpandedRows;
        this.saveExpandedRowsToStorage();
      });
    }
    if (wellCmpId.length > 24) { // Item exists in the DB, API Put
      this._wellConfigService.editTubular(wellCmpToSave).subscribe(() => {
        const index = this.wellComponents.controls.findIndex(c => c.get("id").value == wellCmpId);
        const stringSectionExpanded = this.expandedRows[wellCmpId] ?? false;
        if (stringSectionExpanded == true) {
          this.wellComponents.controls[index].get("stringSectionExpanded").setValue(true, { emitEvent: false });
        }
        this.getExpandedRowKeys();
        this.saveExpandedRowsToStorage();
        // console.log(`Well Config: item updated!`);
      });
    }
    this._messenger.publish(new DataSavedMessage('Well Configuration'));
  }

  public getPlaceholderText(data: any, field: string, depth?: any, column?: string, pipeOutsideDiameter?: number): string {
    const isFilter = ((data?.outerDiameter > 0) || false) || ((data?.outsideDiameter > 0) || false);

    let name = data && data.id ? isFilter ? `${data[field]}` : `${data[field]} (local only)` : "Select";

    if (column == 'pipe') {
      const unit = field === 'weightPerFoot' ? this.units.linearDensity : this.units.shortLengths;
      const precisionOverrides = field === 'weightPerFoot' ? this.wpfPrecisionOverrides() : this.pipeDimensionPrecisionOverrides();
      const displayValue = typeof data?.[field] === 'number'
        ? formatUnitNumber(data[field], unit, this._locale, undefined, precisionOverrides)
        : `${data?.[field]}`;
      name = (data && data.id && data.id.length > 0) ? this.pipeOds.find(x => x.value == data.outsideDiameter) ? displayValue : `${displayValue} (local only)` : 'Select';

      // Temp patch to fix pipe name issue when local only
      if (name.startsWith('null')) {
        this.wellComponents.clear();
        this.getData(false);
      }
    }

    if (column == 'grade') {
      const gradeExists = this.grades.find(x => x.value?.name == data?.name &&
        x.value?.minimumApiYieldStrength == data?.minimumApiYieldStrength && x.value?.youngsModulus == data?.youngsModulus && x.value?.poissonsRatio == data?.poissonsRatio &&
        x.value?.thermalExpansionCoefficient == data?.thermalExpansionCoefficient && x.value?.ultimateTensileStrength == data?.ultimateTensileStrength &&
        x.value?.density == data?.density && x.value?.specificHeatCapacity == data?.specificHeatCapacity && x.value?.temperatureDeration?.name == data?.temperatureDeration?.name);
      if (gradeExists) {
        name = this.grades.find(x => x.value?.name == data?.name).label;
      } else {
        name = this.getLocalOnlyName(depth, column);
      }
    }

    if (column == 'connection') {
      const connectionsExists = this.connections.find(x => x.value?.name === data?.name && x.value?.id === data?.id && x.value?.burstRating === data?.burstRating &&
        x.value?.collapseRating === data?.collapseRating && x.value?.tensionRating === data?.tensionRating && x.value?.compressionRating === data?.compressionRating &&
        x.value?.grade?.name == data?.grade?.name);

      if (data && data.label != 'No Connection' && connectionsExists) {
        name = this.connections.find(x => x.value?.name === data?.name).label;
      } else {
        name = this.getLocalOnlyName(depth, column, pipeOutsideDiameter);
      }
      // else if (connReset) {
      //   name = 'No Connection';
      // }
    }

    return name;
  }

  private getLocalOnlyName(depth: any, column: string, pipeOutsideDiameter?: number): string {
    let pipe = null;

    this.tubularsBackup.forEach(element => {
      element.stringSections.forEach(tubular => {
        if (tubular.bottomMeasuredDepth == depth?.value && (column == 'connection' ? tubular.pipe.outsideDiameter == pipeOutsideDiameter : true)) {
          pipe = tubular;
        }
      });
    });

    let name = pipe && pipe[column]?.name ? `${pipe[column]?.name} (local only)` : "Select";
    if (column == 'connection' && name == 'Select') {
      name = 'No Connection';
    }
    return name;
  }

  public checkStrengthCalculation(data: any): boolean {
    const strengthCalculationOption = data?.value?.strengthCalculationOption == "Api" || !data?.value?.strengthCalculationOption ? true : null;
    this.highCollapsePipeLabel = strengthCalculationOption ? 'API' : 'Non-API';
    return !strengthCalculationOption;
  }

  public getStringSectionsTextHintHover(wellCmpFg: UntypedFormGroup): string {
    const stringSections = wellCmpFg.get("stringSections").value as Tubular[];
    const unit = this._shortLengthUnit == 'in' ? '"' : this._shortLengthUnit;
    const textArray = stringSections.map(x => `${formatUnitNumber(x.pipe.outsideDiameter, this._shortLengthUnit, this._locale, undefined, this.pipeDimensionPrecisionOverrides())}${unit}`)
    return textArray.join(" x ");
  }

  public getStringSectionsTextHint(wellCmpFg: UntypedFormGroup): string {
    const stringSections = wellCmpFg.get("stringSections").value as Tubular[];
    const unit = this._shortLengthUnit == 'in' ? '"' : this._shortLengthUnit;
    return formatUnitNumber(stringSections[0].pipe.outsideDiameter, this._shortLengthUnit, this._locale, undefined, this.pipeDimensionPrecisionOverrides()) + `${unit}`;
  }

  public resetConnection(wellCmpFg: UntypedFormGroup, tIdx: number): void {
    const stringSections = wellCmpFg.get("stringSections") as UntypedFormArray;
    stringSections.controls[tIdx].get("connection").patchValue(null);
  }

  // Private helper methods
  private getPipeCatalogForCtrl(tubularFg: UntypedFormGroup): PipeReference[] {
    const pipe = tubularFg.get("pipe").value as PipeReference;
    return this._pipeCatalog ? this._pipeCatalog.filter(p => p.outsideDiameter == pipe.outsideDiameter) : [];
  }

  public getSortedPipeOptions(rowData: any, parameter: any) {
    const data = (rowData.get('pipeOptions').value ?? []) as PipeReference[];
    const selectedPipe = rowData.get('pipe')?.value as PipeReference;

    let filteredData = data;
    switch (parameter) {
      case 'insideDiameter':
        filteredData = selectedPipe?.weightPerFoot
          ? data.filter(p => p.weightPerFoot == selectedPipe.weightPerFoot)
          : data;
        break;
      case 'wallThickness':
        filteredData = data.filter(p => {
          const matchesWeight = selectedPipe?.weightPerFoot ? p.weightPerFoot == selectedPipe.weightPerFoot : true;
          const matchesInsideDiameter = selectedPipe?.insideDiameter ? p.insideDiameter == selectedPipe.insideDiameter : true;
          return matchesWeight && matchesInsideDiameter;
        });
        break;
      default:
        filteredData = data;
        break;
    }

    return [...filteredData].sort((a, b) => (a[parameter] > b[parameter] ? -1 : 1));
  }

  public getDriftDiameter(pipe: any): string {
    const drift = this._pipeCatalog?.find(p => p.outsideDiameter == pipe.outsideDiameter && p.wallThickness == pipe.wallThickness && p.insideDiameter == pipe.insideDiameter && p.weightPerFoot == pipe.weightPerFoot);
    return drift ? drift.driftDiameter.toFixed(3) : 'N/A';
  }

  public getConnectionGradeFilterPlaceholder(gradeName: string, wellCmpFg: UntypedFormGroup, tIdx: number): string {
    const connectionGrade = wellCmpFg?.get("stringSections")?.value?.[tIdx]?.connectionGradeFilter ?? gradeName;
    if (connectionGrade) {
      ((this.wellComponents.controls[this.wellComponents.controls.indexOf(wellCmpFg)] as UntypedFormGroup).get("stringSections") as UntypedFormArray)
        .controls[tIdx].get("connectionGradeFilter").setValue(connectionGrade, { emitEvent: false });
    }
    return connectionGrade ? connectionGrade : 'Filter';
  }

  public getAvailableConnectionGrades(wellCmpFg: UntypedFormGroup): string[] {
    const stringSection = wellCmpFg?.getRawValue();
    const connectionOptions = this.connections.filter(x => x.value.tubularPipe?.outsideDiameter == stringSection.pipe.outsideDiameter &&
      x.value.tubularPipe?.weightPerFoot == stringSection.pipe.weightPerFoot);
    return [...new Set(connectionOptions?.map(c => c.value?.grade?.name))];
  }

  public onConnectionGradeChange(event: any, wellCmpFg: UntypedFormGroup, tIdx: number, connectionSelect: any): void {
    ((this.wellComponents.controls[this.wellComponents.controls.indexOf(wellCmpFg)] as UntypedFormGroup).get("stringSections") as UntypedFormArray)
      .controls[tIdx].get("connectionGradeFilter").setValue(event.value, { emitEvent: false });

    queueMicrotask(() => {
      connectionSelect?.show?.();
    });
  }

  public onStringTypeChange(event: any, wellCmpFg: UntypedFormGroup, wIdx: number): void {
    if (event.value == 'Liner' && wIdx > 0) {
      const hangerCtrl = wellCmpFg.get("hangerMd");
      hangerCtrl.enable({ emitEvent: false });
      const previousShoeDepth = this.wellComponents.controls[wIdx - 1].get("shoeMd").value;
      const roundedShoeDepth = Math.round(previousShoeDepth / 100) * 100;
      hangerCtrl.setValue(roundedShoeDepth - (this.longLengthUnit == 'ft' ? 100 : 10), { emitEvent: false });
    }
    wellCmpFg.get("hangerMd").updateValueAndValidity({ emitEvent: false });
    wellCmpFg.get("shoeMd").updateValueAndValidity({ emitEvent: false });
    wellCmpFg.get("holeSize").updateValueAndValidity({ emitEvent: false });
  }

  private getPipeOdsForCtrl(wellCmpFg: UntypedFormGroup, stringSectionFg: UntypedFormGroup): { label: string, value: number }[] {
    const wellConfiguration = wellCmpFg?.parent?.getRawValue(); //Using RawValue so we can get the disabled inputs.
    const tubularString = wellCmpFg?.getRawValue();
    const stringSection = stringSectionFg?.getRawValue();
    if (wellConfiguration && tubularString && stringSection) {
      const wellbore = getWellboreOutsideString(tubularString, wellConfiguration);
      const minimumOd = getMinimumDiameterFittingWellbore(tubularString, stringSection, wellbore.wellboreSections);
      return this.pipeOds.filter(kv => kv.value <= minimumOd);
    }
    return this.pipeOds;
  }

  public getConnectionsForCtrl(stringSectionFg: UntypedFormGroup, wellCmpFg: UntypedFormGroup, tIdx: number): { label: string, value: CustomConnectionModel }[] {
    const stringSection = stringSectionFg?.getRawValue();
    if (stringSection && stringSection.pipe) {

      const connGradeFilterValue = wellCmpFg?.get("stringSections")?.value?.[tIdx]?.connectionGradeFilter;
      const connGradeFilter = connGradeFilterValue === 'Filter' ? null : connGradeFilterValue;

      return this.connections.filter(x =>
        x.value.tubularPipe?.outsideDiameter === stringSection.pipe.outsideDiameter &&
        x.value.tubularPipe?.weightPerFoot === stringSection.pipe.weightPerFoot &&
        (!connGradeFilter || x.value.grade?.name === connGradeFilter)
      );
    }
    return [];
  }

  private setPipeSelectOptions(wellCmp: UntypedFormGroup) {
    let idx = 0;
    (wellCmp.get('stringSections') as UntypedFormArray).controls.forEach((ss: UntypedFormGroup) => {
      ss.get('odOptions').patchValue(this.getPipeOdsForCtrl(wellCmp, ss), { emitEvent: false });
      ss.get('pipeOptions').patchValue(this.getPipeCatalogForCtrl(ss), { emitEvent: false });
      ss.get('connectionOptions').patchValue(this.getConnectionsForCtrl(ss, wellCmp, idx), { emitEvent: false });
      idx++;
    });
  }

  // private setSelectedTubularId(tubulars: WellComponent[], curStr: any) {
  //   this.selectedStringId = tubulars.findIndex(x => x.id === curStr.id);
  // }

  private processPipeData(pipeData: PipeReference[]) {
    this._pipeCatalog = pipeData.filter(x => x.discriminator == 'tubularPipe');
    const distinctOds = [...new Set(this._pipeCatalog?.map(p => p.outsideDiameter))];
    this.pipeOds = distinctOds?.map(od => ({ label: od.toString(), value: od }));
  }

  private processGradeData(grades: GradeReference[]) {
    this.grades = grades?.map(g => ({ label: g.name?.toString(), value: g }));
  }

  private processConnectionData(connections: CustomConnectionModel[]): void {
    this.connections = connections?.map(c => ({ label: c.name?.toString(), value: c }));
    // this.connections.unshift({ label: "No Connection", value: null });
  }

  private processTubularData(tubulars: WellComponent[]): void {
    this._wellComponentsData = tubulars;
    this.populateFormData();
  }

  private newWellComponent(tubularFgs: UntypedFormGroup[]): UntypedFormGroup {
    let defaultToc = this.wellComponents.length < 1 && this.mudlineDepth ? this.mudlineDepth : this.wellheadDepth;
    if (this.wellType === 'Land') {
      defaultToc = this.wellComponents.length < 1 ? this.drillFloorElevation : this.wellheadDepth;
    }

    const maxHoleSize = GetShortLengthValueFromInches(48, this._shortLengthUnit);
    const defaultFluid = this.fluids.find(f => f.label == "Freshwater")?.value ?? "";

    const wellComponent = new UntypedFormGroup({
      id: new UntypedFormControl(Utilities.generateFakeGuid()), // Setting a unique id here so that prime-ng p-table has a data key for the expand and collapse functionality.
      sequenceNumber: new UntypedFormControl(0),
      type: new UntypedFormControl("Casing"),
      hangerMd: new UntypedFormControl(this.wellheadDepth, { updateOn: 'blur', validators: [Validators.required, this.zeroOrMin(this.wellheadDepth), hangerAbovePreviousShoe] }),
      shoeMd: new UntypedFormControl("", { updateOn: 'blur', validators: [Validators.required, shoeValidator, Validators.max(this.totalDepth.measuredDepth)] }),
      holeSize: new UntypedFormControl("", { updateOn: 'blur', validators: [Validators.required, holeSizeSmallerThanPrevsId, Validators.max(maxHoleSize)] }),
      hasCement: new UntypedFormControl(true),
      topOfCementMd: new UntypedFormControl(defaultToc, { updateOn: 'blur', validators: [Validators.required, topOfCementMdRequired] }),
      annularFluidId: new UntypedFormControl(defaultFluid, [Validators.required]),
      stringSections: new UntypedFormArray(tubularFgs, { validators: [measuredDepthMustBeGreaterThanPrevious] }),
      stringSectionExpanded: new UntypedFormControl(false),
    }, [revalidateTubulars, stringSectionsMustFitInWellbore]);

    // Events
    this._subscriptions.add(wellComponent.get('type').valueChanges.subscribe(() => this.onWellComponentTypeChange(wellComponent)));
    this._subscriptions.add(wellComponent.get('hasCement').valueChanges.subscribe((v) => this.onHasCementChange(v, wellComponent)));
    wellComponent.get('hangerMd').disable({ emitEvent: false });

    this._subscriptions.add(wellComponent.get('shoeMd').valueChanges.subscribe((v) => this.updateAnnContentsDepths(v, wellComponent)));
    this._subscriptions.add(wellComponent.get('shoeMd').valueChanges.subscribe((v) => this.showPopupIfShoeDepthChanged(wellComponent)));
    this._subscriptions.add(wellComponent.get('topOfCementMd').valueChanges.subscribe((v) => this.updateAnnContentsDepths(v, wellComponent)));
    this._subscriptions.add(wellComponent.get('hasCement').valueChanges.subscribe((v) => this.updateAnnContentsDepths(v, wellComponent)));

    const debouncedWellComponetValueChanges = wellComponent.valueChanges.pipe(debounceTime(300));
    this._subscriptions.add(debouncedWellComponetValueChanges.subscribe((currentValue) => {
      const currentCoreData = this.extractCoreData(currentValue);
      const backup = this.tubularsBackup.find(wc => wc.id === currentValue.id);
      const backupCoreData = this.extractCoreData(backup);
      
      if (backupCoreData && backupCoreData?.hasCement == false) {
        backupCoreData.topOfCementMd = currentCoreData.topOfCementMd;
      }
      if (backupCoreData && !backupCoreData?.holeSize) {
        backupCoreData.holeSize = currentCoreData.holeSize;
      }

      this.tubularsBackup = this.tubularsBackup.map(wc => wc.id === currentValue.id ? currentValue : wc);
      const dataChanged = backupCoreData ? !isEqual(currentCoreData, backupCoreData) : true;
      this.onWellComponentChange(wellComponent, dataChanged);
    }));
    return wellComponent;
  }

  private zeroOrMin(min: number): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;

      if (value === null || value === undefined) {
        return null; // Required handles this
      }

      if (value === 0 || value >= min || Math.abs(value - min) < 0.000001) {
        return null;
      }

      return {
        zeroOrMin: {
          requiredMin: min,
          actual: value
        }
      };
    };
  }

  private extractCoreData(obj: any): any {
    if (!obj) return obj;
    
    return {
      id: obj.id,
      sequenceNumber: obj.sequenceNumber,
      type: obj.type,
      shoeMd: obj.shoeMd,
      holeSize: obj.holeSize,
      hangerMd: obj.hangerMd,
      hasCement: obj.hasCement,
      topOfCementMd: obj.topOfCementMd,
      annularFluidId: obj.annularFluidId,
      stringSections: obj.stringSections?.map(section => ({
        bottomMeasuredDepth: section.bottomMeasuredDepth,
        grade: section.grade,
        pipe: section.pipe ? {
          id: section.pipe.id,
          outsideDiameter: section.pipe.outsideDiameter,
          insideDiameter: section.pipe.insideDiameter,
          weightPerFoot: section.pipe.weightPerFoot,
          wallThickness: section.pipe.wallThickness,
          driftDiameter: section.pipe.driftDiameter
        } : null,
        connection: section.connection,
        nonApiPipe: section.nonApiPipe
      })),
    };
  }

  private newTubular(hangerMd: number) {
    const tubular = new UntypedFormGroup({
      bottomMeasuredDepth: new UntypedFormControl("", { updateOn: 'blur', validators: [Validators.required, greaterThanZero, Validators.min(hangerMd)] }),
      pipe: new UntypedFormGroup({
        id: new UntypedFormControl(""),
        outsideDiameter: new UntypedFormControl("", [Validators.required]),
        insideDiameter: new UntypedFormControl("", [Validators.required]),
        weightPerFoot: new UntypedFormControl("", [Validators.required]),
        wallThickness: new UntypedFormControl("", [Validators.required]),
        driftDiameter: new UntypedFormControl("")
      }),
      grade: new UntypedFormControl("", [Validators.required]),
      connection: new UntypedFormControl(),
      odOptions: new UntypedFormControl(),
      pipeOptions: new UntypedFormControl(),
      connectionOptions: new UntypedFormControl(),
      isNonApiPipeExpanded: new UntypedFormControl(false),
      connectionGradeFilter: new UntypedFormControl(),
      nonApiPipe: new UntypedFormGroup({
        tension: new UntypedFormControl(0, { updateOn: 'blur' }),
        compression: new UntypedFormControl(0, { updateOn: 'blur' }),
        burst: new UntypedFormControl(GetValueFromPsi(12.5, this.units.pressure), { updateOn: 'blur' }),
        collapse: new UntypedFormControl(0, { updateOn: 'blur' }),
        wallToleranceHoop: new UntypedFormControl(12.5, { updateOn: 'blur' }),
        wallToleranceTriaxialAxial: new UntypedFormControl(0, { updateOn: 'blur' }),
        wallToleranceAxial: new UntypedFormControl(0, { updateOn: 'blur' }),
        wallToleranceBurst: new UntypedFormControl(12.5, { updateOn: 'blur' }),
        wallToleranceCollapse: new UntypedFormControl(0, { updateOn: 'blur' }),
        tensionEnabled: new UntypedFormControl(false),
        compressionEnabled: new UntypedFormControl(false),
        burstEnabled: new UntypedFormControl(false),
        collapseEnabled: new UntypedFormControl(false),
      })
    });

    // Events
    // OD is a special case (this call cauese the wellComponent value change to fire before this change handler).
    // OnPipeOdChange fires next, and set the correct pipe size. Having wellComponent debounced by 1 sec. keeps two events from firing.
    this._subscriptions.add(tubular.get('pipe').get('outsideDiameter').valueChanges.subscribe((v) => {
      this.onPipeOdChange(v, tubular)
    }));

    //This is done to sync the pipe dropdown select controls: https://github.com/angular/angular/issues/10036
    this._subscriptions.add(tubular.get('pipe').get('id').valueChanges.subscribe((v) => this.onPipeIdChange(v, tubular)));

    tubular.markAllAsTouched();

    return tubular;
  }

  public onAnnularFluidChange(wellCmpFg: UntypedFormGroup): void {
    if (wellCmpFg.valid) {
      const updatedAnnularFluidDensity = wellCmpFg.get('annularFluidId').value;
      const tubularId = wellCmpFg.get('id').value;
      const previousFluidName = this.tubularStrings.find(x => x.id == tubularId).annularFluid.state.name;
      this._fluidsService.getFluidById(updatedAnnularFluidDensity).subscribe(fluid => {
        const fluidDensity = fluid.state['nominalDensity'];
        if (previousFluidName == 'Freshwater') {
          this.changeDisplacementFluidDensity(fluidDensity, tubularId);
          this.updateAnnulusContents(fluid, tubularId, previousFluidName);
          this.changeInitialConditionsDensity(fluidDensity, tubularId);
          this._allowSave = true;
          this.onWellComponentChange(wellCmpFg, true);
        } else {
          this._allowSave = false;
          this._confirmationService.confirm({
            message: `Do you want to update Displacement Fluid density in Cementing & Landing?<br><br>
                      Note: Please make sure that Annulus Contents are populated correcty after this change.`,
            accept: async () => {
              this.changeDisplacementFluidDensity(fluidDensity, tubularId);
              this.updateAnnulusContents(fluid, tubularId, previousFluidName);
              this._allowSave = true;
              this.onWellComponentChange(wellCmpFg, true);
            },
            reject: () => {
              this._allowSave = true;
              this.onWellComponentChange(wellCmpFg, true);
            }
          });
        }
      });
    }
  }

  private showPopupIfShoeDepthChanged(wellCmpFg: UntypedFormGroup): void {
    if (wellCmpFg.get('shoeMd')?.errors?.error.length > 0) {
      return;
    }
    const tubularId = wellCmpFg.get('id').value;
    if (tubularId.length !== 36) {
      return;
    }
    let previousShoeMd = this.tubularStrings.find(x => x.id == tubularId).shoeMd;
    if (wellCmpFg.get('shoeMd').value >= previousShoeMd) {
      return;
    }
    this._confirmationService.confirm({
      header: 'Shoe depth has been reduced',
      icon: PrimeIcons.INFO_CIRCLE,
      message: `Changing the shoe depth requires double checking the following inputs:<br><br>
                • Packer depths.<br>
                • All depth dependent Pre and Post-Install load case inputs (e.i. Plug or Influx depths).<br>
                • Perforation depths, Drilling operation ranges, etc.<br>`,
      acceptLabel: 'OK',
      rejectVisible: false
    });
  }

  private updateAnnContentsDepths(newDepth: number | boolean, wellCmpFg: UntypedFormGroup): void {
    if (wellCmpFg.invalid) {
      return;
    }
    const tubularId = wellCmpFg.get('id').value;

    this._wellConfigService.getAnnulusContents(tubularId).subscribe(annulusContents => {
      if (!annulusContents) return;

      if (typeof newDepth === 'boolean') {
        annulusContents.annulusContents = newDepth ? [] : [{
          annulusSectionBottom: wellCmpFg.get('shoeMd').value,
          annulusSectionContent: wellCmpFg.get('annularFluidId').value,
          naturalConvection: true,
          radiation: true
        }];
        this._wellConfigService.updateAnnulusContents(annulusContents, tubularId).subscribe();
        return;
      }

      if (wellCmpFg.get('hasCement')?.value == true) {
        newDepth = Math.min(newDepth, wellCmpFg.get('topOfCementMd').value);
      }

      if (!annulusContents.annulusContents?.length) {
        annulusContents.annulusContents = [{
          annulusSectionBottom: newDepth,
          annulusSectionContent: wellCmpFg.get('annularFluidId').value,
          naturalConvection: true,
          radiation: true
        }];
        this._wellConfigService.updateAnnulusContents(annulusContents, tubularId).subscribe();
        return;
      }

      let difference = 0;
      let previousBottom: any = null;

      [...annulusContents.annulusContents].reverse().forEach(ac => {
        if (ac.annulusSectionBottom != newDepth && difference === 0) {
          difference = ac.annulusSectionBottom - +newDepth;
          ac.annulusSectionBottom = newDepth;
          previousBottom = newDepth;
          return;
        }

        if (difference > 0 && previousBottom !== null && ac.annulusSectionBottom > previousBottom) {
          ac.annulusSectionBottom -= difference;
          previousBottom = ac.annulusSectionBottom;
          return;
        }

        previousBottom = ac.annulusSectionBottom;
      });

      this._wellConfigService.updateAnnulusContents(annulusContents, tubularId).subscribe();
    });
  }

  private changeInitialConditionsDensity(newFluidDensity: number, tubularId: string): void {
    this._loadCaseService.getLoadCase(0, tubularId).subscribe(loadCase => {
      const isUserUpdated = Math.abs(loadCase?.internalPressureProfile?.['fluidDensity'] - GetValueFromPpg(8.33, unitsLib[this.units.density].symbol)) > 0.01;
      if (loadCase?.internalPressureProfile?.['fluidDensity'] !== undefined && !isUserUpdated) {
        loadCase.internalPressureProfile['fluidDensity'] = newFluidDensity;
        this._loadCaseService.updateLoadCase(loadCase, tubularId).subscribe();
      }
    });
  }

  private changeDisplacementFluidDensity(newFluidDensity: number, tubularId: string): void {
    this._cementingLandingService.getCementingLanding(tubularId).subscribe(async cementingLanding => {
      cementingLanding.displacementFluidDensity = newFluidDensity;
      this._cementingLandingService.setCementingLanding(cementingLanding, tubularId).subscribe();
    });
  }

  private updateAnnulusContents(fluid: Fluid, tubularId: string, previousFluidName: string): void {
    this._wellConfigService.getAnnulusContents(tubularId).subscribe(annulusContents => {
      if (annulusContents && annulusContents.annulusContents.length === 1) {
        this._fluidsService.getFluidById(annulusContents.annulusContents[0].annulusSectionContent).subscribe(annulusContentsFluid => {
          if (annulusContentsFluid.state.name == previousFluidName) {
            annulusContents.annulusContents[0].annulusSectionContent = fluid.id;
            this._wellConfigService.updateAnnulusContents(annulusContents, tubularId).subscribe();
          }
        });
      } else if (annulusContents && annulusContents.annulusContents.length === 2) {
        const gasValues = ['Oxygen', 'Methane', 'CO2', 'Nitrogen', 'Air'];

        const sections = annulusContents.annulusContents;

        const sources: Observable<any>[] = sections.slice(0, 2).map(section => {
          const value = section.annulusSectionContent;

          const isGas = gasValues.some(g => value?.toLowerCase().startsWith(g.toLowerCase()));

          return isGas ? of({ isGas: true, name: value }) : this._fluidsService.getFluidById(value);
        });

        forkJoin(sources).subscribe(([fluid1, fluid2]) => {
          if (fluid1.isGas) {
            const fluid2Name = gasValues.some(g => fluid2?.state?.name.toLowerCase().startsWith(g.toLowerCase())) ? fluid2.name : fluid2?.state?.name;

            if (fluid2Name === previousFluidName) {
              sections[1].annulusSectionContent = fluid.id;

              this._wellConfigService.updateAnnulusContents(annulusContents, tubularId).subscribe();
            }
          }
        });
      }
    });
  }

  private populateFormData() {
    this._wellComponentsData.forEach((data, idx) => {
      const tubulars = data.stringSections.map(() => this.newTubular(data.hangerMd));
      const wellCmp = this.newWellComponent(tubulars);
      this.wellComponents.push(wellCmp);
      this.wellComponents.controls[idx].patchValue(data, { emitEvent: false });
      this.setPipeSelectOptions(wellCmp);
      this.onWellComponentTypeChange(wellCmp);// This ensures all form controls are greyed out etc on load
      this.onTubularInteractions(wellCmp, false); //This ensures btm depth is greyed out
    });
  }

  ngOnDestroy(): void {
    this._subscriptions?.unsubscribe();
    this.signalRfunc = null;
  }
}
